import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '~/redux/store'
import { BootcampActivityEventId } from '~/bootcamp/types/bootcamp.types'

interface BootcampActivityState {
  correctConsecutiveAnswers: number
  currActivityType: number
  currDifficultyLevel: number
  currRewardLevel: number
  answerState: boolean | null // Three state, 1. null means not attempted, 2. false means wrong & 3. true means passed.
  didUpdateEvent: boolean
  events: BootcampActivityEventId[]
  currentRound: number
  shouldDisableUserInteraction: boolean
  shouldGetEnergyOrbs: boolean
  shouldGetActivityHint: boolean
  shouldAutoValidateAnswer: boolean
  collectedRewards: number
  kudos: number
  isRewardFeedbackScreenOpen: boolean
  shouldResetInput: boolean
}

const initialState: BootcampActivityState = {
  correctConsecutiveAnswers: 0,
  currActivityType: 1,
  currDifficultyLevel: 1,
  currRewardLevel: 1,
  answerState: null,
  didUpdateEvent: false,
  events: [],
  currentRound: 0,
  shouldDisableUserInteraction: false,
  shouldGetEnergyOrbs: false,
  shouldGetActivityHint: false,
  shouldAutoValidateAnswer: false,
  collectedRewards: 0,
  kudos: 0,
  isRewardFeedbackScreenOpen: false,
  shouldResetInput: false,
}

const slice = createSlice({
  name: 'bootcamp-activity',
  initialState,
  reducers: {
    reinitializeState: (state) => ({ ...state, ...initialState }),

    incrementCorrectConsecutiveAnswers: (state) => ({
      ...state,
      correctConsecutiveAnswers: state.correctConsecutiveAnswers + 1,
    }),

    // used by larger more time consuming activities to make you not have to do 3 of them in a row
    giveAdditional2CorrectConsecutiveAnswers: (state) => ({
      ...state,
      correctConsecutiveAnswers: state.correctConsecutiveAnswers + 2,
    }),

    incrementDifficultyLevel: (state) => ({
      ...state,
      currDifficultyLevel: state.currDifficultyLevel + 1,
    }),

    incrementRewardLevel: (state) => ({
      ...state,
      currRewardLevel: state.currRewardLevel + 1,
    }),

    resetCorrectConsecutiveAnswers: (state) => ({
      ...state,
      correctConsecutiveAnswers: 0,
    }),

    setBootcampActivityEvents: (state, action: PayloadAction<BootcampActivityEventId | BootcampActivityEventId[]>) => ({
      ...state,
      events: Array.isArray(action.payload) ? action.payload : [...state.events, action.payload],
    }),

    setCurrentRound: (state, action: PayloadAction<number>) => ({
      ...state,
      currentRound: action.payload,
    }),

    setCurrActivityType: (state, action: PayloadAction<number>) => ({
      ...state,
      currActivityType: action.payload,
    }),

    setCurrDifficultyLevel: (state, action: PayloadAction<number>) => ({
      ...state,
      currDifficultyLevel: action.payload,
    }),

    setCurrRewardLevel: (state, action: PayloadAction<number>) => ({
      ...state,
      currRewardLevel: action.payload,
    }),

    setCollectedRewards: (state, action: PayloadAction<number>) => ({
      ...state,
      collectedRewards: action.payload,
    }),

    setAnswerState: (state, action: PayloadAction<boolean | null>) => ({
      ...state,
      answerState: action.payload,
    }),

    setDidUpdateEvent: (state, action: PayloadAction<boolean>) => ({
      ...state,
      didUpdateEvent: action.payload,
    }),

    setShouldDisableUserInteraction: (state, action: PayloadAction<boolean>) => ({
      ...state,
      shouldDisableUserInteraction: action.payload,
    }),
    setShouldGetEnergyOrbs: (state, action: PayloadAction<boolean>) => ({
      ...state,
      shouldGetEnergyOrbs: action.payload,
    }),
    setShouldGetActivityHint: (state, action: PayloadAction<boolean>) => ({
      ...state,
      shouldGetActivityHint: action.payload,
    }),
    setShouldAutoValidateAnswer: (state, action: PayloadAction<boolean>) => ({
      ...state,
      shouldAutoValidateAnswer: action.payload,
    }),
    setKudos: (state, action: PayloadAction<number>) => ({
      ...state,
      kudos: action.payload,
    }),
    setIsRewardFeedbackScreenOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isRewardFeedbackScreenOpen: action.payload,
    }),
    setShouldResetInput: (state, action: PayloadAction<boolean>) => ({
      ...state,
      shouldResetInput: action.payload,
    }),
  },
})

export const selectBootcampActivityEvents = (state: RootState): BootcampActivityState['events'] => {
  return state.bootcampActivity.events
}

export const selectCurrentRound = (state: RootState): BootcampActivityState['currentRound'] => {
  return state.bootcampActivity.currentRound
}

export const selectCorrectConsecutiveAnswers = (
  state: RootState,
): BootcampActivityState['correctConsecutiveAnswers'] => {
  return state.bootcampActivity.correctConsecutiveAnswers
}

export const selectCurrentActivityType = (state: RootState): BootcampActivityState['currActivityType'] => {
  return state.bootcampActivity.currActivityType
}

export const selectCurrentDifficultyLevel = (state: RootState): BootcampActivityState['currDifficultyLevel'] => {
  return state.bootcampActivity.currDifficultyLevel
}

export const selectCurrentRewardLevel = (state: RootState): BootcampActivityState['currRewardLevel'] => {
  return state.bootcampActivity.currRewardLevel
}

export const selectAnswerState = (state: RootState): BootcampActivityState['answerState'] => {
  return state.bootcampActivity.answerState
}

export const selectDidUpdateEvent = (state: RootState): BootcampActivityState['didUpdateEvent'] => {
  return state.bootcampActivity.didUpdateEvent
}

export const selectCollectedRewards = (state: RootState): BootcampActivityState['collectedRewards'] => {
  return state.bootcampActivity.collectedRewards
}

export const selectShouldDisableUserInteraction = (
  state: RootState,
): BootcampActivityState['shouldDisableUserInteraction'] => {
  return state.bootcampActivity.shouldDisableUserInteraction
}

export const selectShouldGetEnergyOrbs = (state: RootState): BootcampActivityState['shouldGetEnergyOrbs'] => {
  return state.bootcampActivity.shouldGetEnergyOrbs
}

export const selectShouldGetActivityHint = (state: RootState): BootcampActivityState['shouldGetActivityHint'] => {
  return state.bootcampActivity.shouldGetActivityHint
}

export const selectShouldAutoValidateAnswer = (state: RootState): BootcampActivityState['shouldAutoValidateAnswer'] => {
  return state.bootcampActivity.shouldAutoValidateAnswer
}

export const selectKudos = (state: RootState): BootcampActivityState['kudos'] => {
  return state.bootcampActivity.kudos
}

export const selectIsRewardFeedbackScreenOpen = (
  state: RootState,
): BootcampActivityState['isRewardFeedbackScreenOpen'] => {
  return state.bootcampActivity.isRewardFeedbackScreenOpen
}

export const selectShouldResetInput = (state: RootState): BootcampActivityState['shouldResetInput'] => {
  return state.bootcampActivity.shouldResetInput
}

export const {
  incrementCorrectConsecutiveAnswers: incrementCorrectConsecutiveAnswersActionCreator,
  giveAdditional2CorrectConsecutiveAnswers: giveAdditional2CorrectConsecutiveAnswersActionCreator,
  incrementDifficultyLevel: incrementDifficultyLevelActionCreator,
  incrementRewardLevel: incrementRewardLevelActionCreator,
  resetCorrectConsecutiveAnswers: resetCorrectConsecutiveAnswersActionCreator,
  reinitializeState: reinitializeStateActionCreator,
  setBootcampActivityEvents: setBootcampActivityEventsActionCreator,
  setCurrentRound: setCurrentRoundActionCreator,
  setCurrActivityType: setCurrActivityTypeActionCreator,
  setCurrDifficultyLevel: setCurrDifficultyLevelActionCreator,
  setCurrRewardLevel: setCurrRewardLevelActionCreator,
  setCollectedRewards: setCollectedRewardsCreator,
  setAnswerState: setAnswerStateActionCreator,
  setDidUpdateEvent: setDidUpdateEventActionCreator,
  setShouldDisableUserInteraction: setShouldDisableUserInteractionActionCreator,
  setShouldGetEnergyOrbs: setShouldGetEnergyOrbsActionCreator,
  setShouldGetActivityHint: setShouldGetActivityHintActionCreator,
  setShouldAutoValidateAnswer: setShouldAutoValidateAnswerActionCreator,
  setKudos: setKudosActionCreator,
  setIsRewardFeedbackScreenOpen: setIsRewardFeedbackScreenOpenActionCreator,
  setShouldResetInput: setShouldResetInputActionCreator,
} = slice.actions

export default slice.reducer
